import { FC, useMemo, useState } from 'react';
import {
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  useTasksControllerSetAssigneeMutation,
  useWorkspacesControllerGetWorkspaceByIdQuery,
} from 'src/app/services/api.generated';
import { FormDialog } from 'src/components/molecules/FormDialog';
import { memberDisplayName } from 'src/utils/displayUsername';

type AssignTaskDialogProps = {
  open: boolean;
  onClose: () => void;
  taskId: string;
  workspaceId?: string;
  currentAssigneeId?: string | null;
  onAssigned?: (assigneeId: string | null) => void;
};

export const AssignTaskDialog: FC<AssignTaskDialogProps> = ({
  open,
  onClose,
  taskId,
  workspaceId,
  currentAssigneeId,
  onAssigned,
}) => {
  const { t } = useTranslation();
  const [pendingUsername, setPendingUsername] = useState<string | null>(null);

  const { data: workspace, isLoading: workspaceLoading } =
    useWorkspacesControllerGetWorkspaceByIdQuery(
      { id: workspaceId || '' },
      { skip: !open || !workspaceId }
    );

  const [setAssignee, { isLoading: assignLoading }] =
    useTasksControllerSetAssigneeMutation();

  const members = useMemo(() => {
    if (!workspace) return [];
    return Array.from(
      new Set([
        ...(workspace.workspaceUsers || []),
        ...(workspace.workspaceMaintainers || []),
        ...(workspace.creator ? [workspace.creator] : []),
      ])
    ).sort((a, b) => a.localeCompare(b));
  }, [workspace]);

  const applyAssignee = async (assigneeId: string | null) => {
    setPendingUsername(assigneeId);
    try {
      await setAssignee({
        assignTaskDto: { id: taskId, assigneeId },
      }).unwrap();
      toast.success(t('assignTask.success'));
      onAssigned?.(assigneeId);
      onClose();
    } catch {
      toast.error(t('assignTask.failed'));
    } finally {
      setPendingUsername(null);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={() => onClose()}
      title={t('assignTask')}
      maxWidth="xs"
      hideActions={false}
      submitText={undefined}
      onSubmit={undefined}
      cancelText={t('cancel')}
    >
      <Box sx={{ minHeight: 80 }}>
        {!workspaceId ? (
          <ListItemText primary={t('noAssignee')} />
        ) : workspaceLoading ? (
          <CircularProgress size={24} />
        ) : (
          <List dense disablePadding>
            <ListItemButton
              selected={!currentAssigneeId}
              disabled={assignLoading}
              onClick={() => applyAssignee(null)}
            >
              <ListItemText primary={t('unassign')} />
              {assignLoading && pendingUsername === null && (
                <CircularProgress size={16} />
              )}
            </ListItemButton>
            {members.map((username) => (
              <ListItemButton
                key={username}
                selected={currentAssigneeId === username}
                disabled={assignLoading}
                onClick={() => applyAssignee(username)}
              >
                <ListItemText
                  primary={memberDisplayName(
                    username,
                    workspace?.memberEmails,
                    workspace?.userDisplay
                  ) || '—'}
                />
                {assignLoading && pendingUsername === username && (
                  <CircularProgress size={16} />
                )}
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </FormDialog>
  );
};
